import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import TableCell from '@mui/material/TableCell';
import NoResults from '@Components/NoResults/NoResults';
import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import styled from 'styled-components';
import api from '@Services/api';
import MainTable, { MainTableRow } from '@Components/MainTable/MainTable';
import { TableHeader } from '@Features/generics/generics.type';
import { FlexLayout } from '@Styled/utilities';
import { useTypedSelector } from '@Features/store';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import { ImportsSlice, importsGetAll } from '@Pages/Imports/Slice/Imports.slice';
import { useDispatch } from 'react-redux';
import {
	Icon,
	RadioGroup,
	Radio,
	FormControlLabel,
	Button,
} from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { CSVLink } from 'react-csv';
import usePlatformMode from '@Hooks/usePlatformMode';
import { Import, SuccessSyncData } from './Types/Imports.types';
// Relative on purpose: this file is shared into the instructor build by
// re-export, and an @Pages alias would resolve into that app's tree.
import useAdminActionPermissions from '../StandaloneCourses/useAdminActionPermissions';

/**
 * The Imports tab. This file is the single implementation for both portals —
 * the instructor app re-exports it. Per-app modules (slice, api, hooks,
 * snackbar) come in through aliases so each build gets its own.
 *
 * Flow: pick an action, upload the file, Import. The backend answers with the
 * audit row already in the table as `pending`; the rows are applied after the
 * response, so Refresh (or a reload) shows the outcome. The Success CSV is
 * the rows that applied, as uploaded; the Failed CSV is the rows that did not,
 * laid out like the upload with the reason in the `flag` column.
 */

type ImportAction = {
	value: string;
	label: string;
	/** Platform-mode flag the action depends on; null = offered in every mode. */
	requires: 'classrooms' | 'standalone' | 'bundles' | null;
	accept: '.csv' | '.json';
};

const IMPORT_ACTIONS: ImportAction[] = [
	{ value: 'classroom-enroll', label: 'Classroom enrollment', requires: 'classrooms', accept: '.csv' },
	{ value: 'course-enroll', label: 'Course enrollment', requires: 'classrooms', accept: '.csv' },
	{ value: 'banned', label: 'Classroom ban', requires: 'classrooms', accept: '.csv' },
	{ value: 'standalone-enroll', label: 'Standalone course enrollment', requires: 'standalone', accept: '.csv' },
	{ value: 'bundle-enroll', label: 'Bundle enrollment', requires: 'bundles', accept: '.csv' },
	{ value: 'instructor-codes', label: 'Instructor codes', requires: null, accept: '.csv' },
	// Attendance sync records are classroom-keyed.
	{ value: 'sync', label: 'Sync Records', requires: 'classrooms', accept: '.json' },
];

const tableHeaders: Array<TableHeader> = [
	{
		displayName: 'ID',
		fieldName: 'id',
		width: '8%',
	},
	{
		displayName: 'Status',
		fieldName: 'status',
		width: '9%',
	},
	{
		displayName: 'Imported no.',
		fieldName: 'imported_count',
		width: '9%',
	},
	{
		displayName: 'Success no.',
		fieldName: 'success_count',
		width: '9%',
	},
	{
		displayName: 'Failed no.',
		fieldName: 'failed_count',
		width: '9%',
	},
	{
		displayName: 'Description',
		fieldName: 'description',
		width: '22%',
	},
	{
		displayName: 'Import date',
		fieldName: 'created_at',
		width: '12%',
	},
	{
		displayName: 'Success data',
		fieldName: 'success_data',
		width: '11%',
	},
	{
		displayName: 'Failed data',
		fieldName: 'failed_data',
		width: '11%',
	},
];

// Downloaded files keep the upload layout (raw column names), so a success
// file can be re-imported as-is and a failed file reads like the sheet the
// operator sent — with the reason where the flag was.
const enrollmentSuccessHeader = [
	{ key: 'username', label: 'username' },
	{ key: 'identifier', label: 'identifier' },
	{ key: 'flag', label: 'flag' },
];
const enrollmentFailedHeader = [
	{ key: 'username', label: 'username' },
	{ key: 'identifier', label: 'identifier' },
	{ key: 'reason', label: 'flag' },
];

const instructorCodesSuccessHeader = [
	{ key: 'code', label: 'code' },
	{ key: 'label', label: 'label' },
	{ key: 'group_name', label: 'group_name' },
];
const instructorCodesFailedHeader = [
	{ key: 'code', label: 'code' },
	{ key: 'label', label: 'label' },
	{ key: 'group_name', label: 'group_name' },
	{ key: 'reason', label: 'reason' },
];

const isInstructorCodesImport = (item: Import) =>
	item.description === 'instructor codes import';

const safeParse = <T,>(json: string | null | undefined): T | null => {
	if (!json) return null;
	try {
		return JSON.parse(json) as T;
	} catch {
		return null;
	}
};

/** Rows imported before success_data was written fall back to the upload. */
const successRows = (item: Import) =>
	safeParse<any[]>(item.success_data ?? item.imported_data) ?? [];
const failedRows = (item: Import) => safeParse<any[]>(item.failed_data) ?? [];

const ImportsTable: React.FC = () => {
	const { meta, filters, fetchAll } = useTypedSelector((state) => state.Imports);
	const [fileValue, setFileValue] = useState<File | null>(null);
	const [importing, setImporting] = useState(false);
	const { can } = useAdminActionPermissions();
	const { classroomsEnabled, standaloneEnabled, bundlesEnabled } = usePlatformMode();

	const dispatch = useDispatch();
	const { displaySnackbar } = useSnackbar();
	const inputFileRef = useRef<HTMLInputElement>(null);

	// Only the actions this platform mode actually supports.
	const availableActions = useMemo(
		() =>
			IMPORT_ACTIONS.filter((item) => {
				if (item.requires === 'classrooms') return classroomsEnabled;
				if (item.requires === 'standalone') return standaloneEnabled;
				if (item.requires === 'bundles') return bundlesEnabled;
				return true;
			}),
		[classroomsEnabled, standaloneEnabled, bundlesEnabled]
	);
	const [action, setAction] = useState<string>(IMPORT_ACTIONS[0].value);
	const selectedAction =
		availableActions.find((item) => item.value === action) ?? availableActions[0];
	// Flags load after mount: keep the selection on an offered action.
	useEffect(() => {
		if (selectedAction && selectedAction.value !== action) {
			setAction(selectedAction.value);
			setFileValue(null);
		}
	}, [action, selectedAction]);

	const acceptedFileType = selectedAction?.accept ?? '.csv';
	const handleChooseFile = () => {
		if (inputFileRef.current) {
			inputFileRef.current.value = '';
			inputFileRef.current.click();
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		const file = e.target.files.item(0);
		if (!file) return;
		// By extension, not MIME: Excel-saved CSVs on Windows report
		// application/vnd.ms-excel and used to be refused here.
		if (file.name.toLowerCase().endsWith(acceptedFileType)) setFileValue(file);
		else {
			displaySnackbar('error', `Only ${acceptedFileType} files are accepted`);
			setFileValue(null);
		}
	};

	const refreshList = useCallback(() => {
		ImportsSlice.getInstance().reFetchGetAll();
	}, []);

	const importFile = async () => {
		if (!fileValue || !selectedAction) {
			displaySnackbar('error', 'Please upload file');
			return;
		}
		const formData = new FormData();
		formData.append('file', fileValue);
		setImporting(true);
		try {
			const { data } = await api.post(`import/${selectedAction.value}`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});
			const created = data?.data;
			refreshList();
			displaySnackbar(
				'success',
				created?.id
					? `Import #${created.id} queued (${created.imported_count ?? '?'} rows) — refresh to see the result`
					: 'Import queued — refresh to see the result'
			);
			setFileValue(null);
		} catch (error: any) {
			displaySnackbar('error', error?.response?.data?.message || 'Import failed');
		} finally {
			setImporting(false);
		}
	};

	const onPageRequestCallback = useCallback(
		({
			activePage,
			endDate,
			filters,
			query,
			sortBy,
			startDate,
			perPage,
		}: onPageChangeRequestMeta) => {
			dispatch(
				importsGetAll({
					filters,
					page: activePage,
					perPage: perPage ?? 10,
					query,
					sortBy,
					from: startDate,
					to: endDate,
				})
			);
		},
		[]
	);

	const hasSuccessData = (item: Import) => {
		if (item.module === 'sync') return !!safeParse(item.success_data);
		return successRows(item).length > 0;
	};
	const hasFailedData = (item: Import) => {
		if (item.module === 'sync') return !!safeParse(item.failed_data);
		return failedRows(item).length > 0;
	};

	return (
		<>
			{can('c-imp') && (
				<>
					<FlexLayout flexDirection="column" mb="40px">
						<input
							type="file"
							accept={acceptedFileType}
							hidden
							onChange={handleFileChange}
							ref={inputFileRef}
						/>
						<UploadButton
							onClick={handleChooseFile}
							startIcon={<Icon>upload_file</Icon>}
							variant="outlined"
							color="primary"
						>
							Upload File
						</UploadButton>
						{fileValue && (
							<FlexLayout
								minWidth="250px"
								width="fit-content"
								marginLeft="10px"
								justifyContent="space-between"
							>
								<p style={{ fontSize: '18px' }}>{fileValue.name}</p>
								<EdIcon
									style={{
										color: '#f74343',
										marginLeft: '10px',
										marginTop: '-5px',
										cursor: 'pointer',
									}}
									fontSize="small"
									onClick={() => setFileValue(null)}
								>
									<Icon>delete</Icon>
								</EdIcon>
							</FlexLayout>
						)}
					</FlexLayout>
					<FlexLayout mb="50px">
						<RadioGroup
							row
							value={selectedAction?.value ?? ''}
							style={{ flex: 1.5 }}
							onChange={(event, value) => {
								setAction(value);
								setFileValue(null);
							}}
						>
							{availableActions.map((item) => (
								<FormControlLabel
									key={item.value}
									value={item.value}
									label={item.label}
									control={<Radio />}
								/>
							))}
						</RadioGroup>
						<FlexLayout justifyContent="flex-end">
							<RoundButton
								onClick={refreshList}
								sx={{ marginRight: '1rem' }}
								startIcon={<EdIcon>refresh</EdIcon>}
								variant="outlined"
							>
								Refresh
							</RoundButton>
							<RoundButton
								onClick={importFile}
								disabled={importing}
								sx={{ marginRight: '1rem' }}
								startIcon={<EdIcon>redo</EdIcon>}
								variant="contained"
							>
								Import
							</RoundButton>
						</FlexLayout>
					</FlexLayout>
				</>
			)}
			<MainTable
				tableFilters={filters}
				tableHeads={tableHeaders}
				totalPages={meta?.last_page ?? 0}
				onPageChangeRequest={onPageRequestCallback}
				RowsperPage={meta?.per_page}
				pageNum={meta?.current_page}
				$hasActions
				total={meta?.total}
				renderItems={() => {
					if (fetchAll.length === 0)
						return (
							<MainTableRow>
								<TableCell colSpan={tableHeaders.length}>
									<NoResults />
								</TableCell>
							</MainTableRow>
						);
					return fetchAll.map((importValue) => {
						return (
							<MainTableRow key={importValue?.id} hover>
								<TableCell>{importValue?.id || '-'}</TableCell>
								<TableCell>{importValue?.status || '-'}</TableCell>
								<TableCell>{importValue?.imported_count ?? '-'}</TableCell>
								<TableCell>{importValue?.success_count ?? '-'}</TableCell>
								<TableCell>{importValue?.failed_count ?? '-'}</TableCell>
								<TableCell
									style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
								>
									{importValue?.description || '-'}
								</TableCell>
								<TableCell>
									<DateTimeFormatter date={importValue.created_at} />
								</TableCell>
								<TableCell>
									{hasSuccessData(importValue) ? (
										<DownloadCSV item={importValue} type="success" />
									) : (
										<StyledImportDataButton
											ImportedFlag={true}
											onClick={() => {
												displaySnackbar('error', 'No data to download');
											}}
										>
											<EdIcon>download</EdIcon>
										</StyledImportDataButton>
									)}
								</TableCell>
								<TableCell>
									{hasFailedData(importValue) ? (
										<DownloadCSV item={importValue} type="failed" />
									) : (
										<StyledImportDataButton
											ImportedFlag={false}
											onClick={() => {
												displaySnackbar('error', 'No data to download');
											}}
										>
											<EdIcon>download</EdIcon>
										</StyledImportDataButton>
									)}
								</TableCell>
							</MainTableRow>
						);
					});
				}}
			/>
		</>
	);
};

const StyledImportDataButton = styled.button<{ ImportedFlag: boolean }>`
	width: 34px;
	height: 34px;
	border-radius: 6px;
	padding-top: 5px;
	border: none;
	cursor: pointer;
	background-color: ${(props) => (props.ImportedFlag ? '#54C0FC' : '#f74343')};
	color: white;
`;

const UploadButton = styled(Button)`
	width: fit-content;
	margin-bottom: 20px;
`;

export default ImportsTable;

type Props = {
	item: Import;
	type: 'success' | 'failed';
};
const DownloadCSV = ({ item, type }: Props) => {
	const convertToCSV = () => {
		const parsed = safeParse<SuccessSyncData>(
			type === 'success' ? item.success_data : item.failed_data
		);

		const centerAttendanceHeaders = [
			['Center Attendance', '', '', '', '', ''],
			[
				'Course',
				'Student',
				'Type',
				'Classroom',
				type === 'failed' ? ['Reason'] : '',
			],
		];
		const enrollmentHeaders = [
			['Enrollment', '', '', '', '', ''],
			['Course', 'Student', 'Type', type === 'failed' ? ['Reason'] : '', ''],
		];
		const centerAttendanceData = (parsed?.center_attendances ?? []).map((data) => [
			data.courseId,
			data.studentId,
			data._status,
			data.classroomId,
			type === 'failed' ? data.message : '',
		]);
		const enrollmentData = (parsed?.enroll_courses ?? []).map((data) => [
			data.course_id,
			data.user_id,
			data._status,
			type === 'failed' ? data.message : '',
			'',
		]);

		const csvValues = [
			centerAttendanceHeaders.join('\n'),
			centerAttendanceData.join('\n'),
			enrollmentHeaders.join('\n'),
			enrollmentData.join('\n'),
		]
			.filter((_) => _.length > 0)
			.join('\n');

		return csvValues;
	};

	const filename =
		`${type === 'success' ? 'ImportedData' : 'FailedData'}` + item.id + '.csv';

	const downloadCSV = () => {
		const csvData = new Blob([convertToCSV()], { type: 'text/csv' });
		const csvURL = URL.createObjectURL(csvData);
		const link = document.createElement('a');
		link.href = csvURL;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};
	if (item.module === 'sync')
		return (
			<StyledImportDataButton
				onClick={downloadCSV}
				ImportedFlag={type === 'success'}
			>
				<EdIcon>download</EdIcon>
			</StyledImportDataButton>
		);

	const codes = isInstructorCodesImport(item);
	const headers =
		type === 'success'
			? codes
				? instructorCodesSuccessHeader
				: enrollmentSuccessHeader
			: codes
			? instructorCodesFailedHeader
			: enrollmentFailedHeader;
	return (
		<CSVLink
			data={type === 'success' ? successRows(item) : failedRows(item)}
			filename={filename}
			headers={headers}
		>
			<StyledImportDataButton ImportedFlag={type === 'success'}>
				<EdIcon>download</EdIcon>
			</StyledImportDataButton>
		</CSVLink>
	);
};
